import { useState, useEffect } from "react";
import {
    useCreateFlyerType,
    useUpdateFlyerType,
    useAvailableFlyerTypes,
    useFlyerTypes,
    type CampaignFlyer,
    type FlyerTypeCreateRequest,
    type FlyerTypeUpdateRequest,
} from "../api/campaigns";
import { ImageUpload } from "../components/ImageUpload";
import { showError, showSuccess } from "../shared/toast";
import "./flyer-type-form.css";

interface FlyerTypeFormProps {
    campaignId: string;
    campaignFlyer?: CampaignFlyer;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export const FlyerTypeForm = ({
    campaignId,
    campaignFlyer,
    onSuccess,
    onCancel,
}: FlyerTypeFormProps): JSX.Element => {
    const isEditMode = !!campaignFlyer;
    const [useExistingType, setUseExistingType] = useState(
        campaignFlyer ? !!campaignFlyer.flyerTypeId : true
    );
    const [formData, setFormData] = useState<FlyerTypeCreateRequest>({
        flyerTypeId: campaignFlyer?.flyerTypeId || "",
        name: campaignFlyer?.name || "",
        summary: campaignFlyer?.summary || "",
        imageUrl:
            campaignFlyer?.imageUrl ||
            campaignFlyer?.flyerType?.imageUrl ||
            "",
        weightGrams: campaignFlyer?.weightGrams ?? undefined,
        isDefault: campaignFlyer?.isDefault || false,
        priority: campaignFlyer?.priority ?? 0,
        plannedQuantity: campaignFlyer?.plannedQuantity ?? undefined,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { data: availableFlyerTypes = [], isLoading: isLoadingFlyerTypes } =
        useAvailableFlyerTypes();
    const { data: existingCampaignFlyers = [] } = useFlyerTypes(
        !isEditMode ? campaignId : null
    );
    const createMutation = useCreateFlyerType(campaignId);
    const updateMutation = useUpdateFlyerType(campaignId);

    useEffect(() => {
        if (campaignFlyer) {
            setUseExistingType(!!campaignFlyer.flyerTypeId);
            setFormData({
                flyerTypeId: campaignFlyer.flyerTypeId,
                name: campaignFlyer.name || "",
                summary: campaignFlyer.summary || "",
                imageUrl:
                    campaignFlyer.imageUrl ||
                    campaignFlyer.flyerType?.imageUrl ||
                    "",
                weightGrams: campaignFlyer.weightGrams ?? undefined,
                isDefault: campaignFlyer.isDefault,
                priority: campaignFlyer.priority ?? 0,
                plannedQuantity: campaignFlyer.plannedQuantity ?? undefined,
            });
        }
    }, [campaignFlyer]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (useExistingType) {
            if (!formData.flyerTypeId) {
                newErrors.flyerTypeId = "Flygblad är obligatoriskt";
            }
        } else if (!formData.name?.trim()) {
            newErrors.name = "Namn är obligatoriskt";
        }
        if (formData.plannedQuantity !== undefined && formData.plannedQuantity < 0) {
            newErrors.plannedQuantity = "Planerat antal måste vara 0 eller större";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            if (isEditMode && campaignFlyer) {
                const updateRequest: FlyerTypeUpdateRequest = {
                    isDefault: formData.isDefault,
                    priority: formData.priority,
                    plannedQuantity: formData.plannedQuantity,
                    imageUrl: formData.imageUrl || undefined,
                };
                if (!campaignFlyer.flyerTypeId) {
                    updateRequest.name = formData.name;
                    updateRequest.summary = formData.summary;
                    updateRequest.weightGrams = formData.weightGrams;
                }
                await updateMutation.mutateAsync({
                    id: campaignFlyer.id,
                    ...updateRequest,
                });
            } else {
                const createRequest: FlyerTypeCreateRequest = {
                    flyerTypeId: useExistingType ? formData.flyerTypeId : undefined,
                    name: useExistingType ? undefined : formData.name,
                    summary: useExistingType ? undefined : formData.summary,
                    imageUrl: formData.imageUrl || undefined,
                    weightGrams: useExistingType ? undefined : formData.weightGrams,
                    isDefault: formData.isDefault,
                    priority: formData.priority,
                    plannedQuantity: formData.plannedQuantity,
                };
                await createMutation.mutateAsync(createRequest);
            }
            onSuccess?.();
            if (isEditMode) {
                showSuccess("Flygbladet har uppdaterats");
            } else {
                showSuccess("Flygbladet har lagts till");
            }
        } catch (error: any) {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                const message =
                    error?.response?.data?.message || "Ett fel uppstod";
                showError("Kunde inte spara flygbladet", message);
            }
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (name === "priority" || name === "plannedQuantity") {
            const numValue = value === "" ? undefined : parseInt(value, 10);
            setFormData((prev) => ({ ...prev, [name]: numValue }));
        } else if (name === "weightGrams") {
            const numValue = value === "" ? undefined : parseFloat(value);
            setFormData((prev) => ({ ...prev, [name]: numValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    // Filter out already added flyer types (only in create mode)
    const existingFlyerTypeIds = new Set(
        existingCampaignFlyers
            .map((cf) => cf.flyerTypeId)
            .filter((id): id is string => Boolean(id))
    );
    const availableOptions = isEditMode
        ? availableFlyerTypes
        : availableFlyerTypes.filter(
            (ft) => !existingFlyerTypeIds.has(ft.id)
        );

    return (
        <form className="flyer-type-form" onSubmit={handleSubmit}>
            <h3>{isEditMode ? "Redigera flygblad i kampanj" : "Lägg till flygblad till kampanj"}</h3>
            {!isEditMode && (
                <p className="form-help-text" style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1rem" }}>
                    Välj ett befintligt flygblad eller skapa ett kampanjflygblad utan koppling.
                    För att skapa ett nytt globalt flygblad, gå till{" "}
                    <a href="/admin/flyer-types" style={{ color: "#93c5fd", textDecoration: "underline" }}>
                        Flygblad
                    </a>.
                </p>
            )}

            {!isEditMode && (
                <div className="form-group">
                    <label>
                        <input
                            type="radio"
                            name="flyerSource"
                            checked={useExistingType}
                            onChange={() => {
                                setUseExistingType(true);
                                setFormData((prev) => ({
                                    ...prev,
                                    flyerTypeId: prev.flyerTypeId || "",
                                    name: "",
                                    summary: "",
                                    weightGrams: undefined,
                                }));
                            }}
                        />
                        <span>Välj befintligt flygblad</span>
                    </label>
                    <label>
                        <input
                            type="radio"
                            name="flyerSource"
                            checked={!useExistingType}
                            onChange={() => {
                                setUseExistingType(false);
                                setFormData((prev) => ({
                                    ...prev,
                                    flyerTypeId: "",
                                }));
                            }}
                        />
                        <span>Skapa kampanjflygblad (utan typ)</span>
                    </label>
                </div>
            )}

            {useExistingType ? (
                <div className="form-group">
                    <label htmlFor="flyerTypeId">
                        Flygblad <span aria-label="obligatoriskt">*</span>
                    </label>
                    <select
                        id="flyerTypeId"
                        name="flyerTypeId"
                        value={formData.flyerTypeId}
                        onChange={handleChange}
                        disabled={isEditMode || isLoadingFlyerTypes}
                        required
                        aria-required="true"
                        aria-invalid={!!errors.flyerTypeId}
                        aria-describedby={errors.flyerTypeId ? "flyerTypeId-error" : undefined}
                        className={errors.flyerTypeId ? "error-field" : ""}
                    >
                        <option value="">
                            {isLoadingFlyerTypes ? "Laddar..." : "Välj flygblad..."}
                        </option>
                        {availableOptions.map((ft) => (
                            <option key={ft.id} value={ft.id}>
                                {ft.name}
                            </option>
                        ))}
                    </select>
                    {availableOptions.length === 0 && !isLoadingFlyerTypes && (
                        <p className="form-help-text" style={{ color: "#94a3b8", fontSize: "0.875rem", marginTop: "0.5rem" }}>
                            {availableFlyerTypes.length === 0
                                ? "Inga flygblad finns i systemet. Skapa flygblad först."
                                : "Alla tillgängliga flygblad är redan tillagda i denna kampanj."}
                        </p>
                    )}
                    {errors.flyerTypeId && (
                        <span id="flyerTypeId-error" role="alert" className="error-message">
                            {errors.flyerTypeId}
                        </span>
                    )}
                </div>
            ) : (
                <>
                    <div className="form-group">
                        <label htmlFor="name">
                            Namn <span aria-label="obligatoriskt">*</span>
                        </label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            value={formData.name ?? ""}
                            onChange={handleChange}
                            aria-invalid={!!errors.name}
                            aria-describedby={errors.name ? "name-error" : undefined}
                            className={errors.name ? "error-field" : ""}
                        />
                        {errors.name && (
                            <span id="name-error" role="alert" className="error-message">
                                {errors.name}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="summary">Sammanfattning</label>
                        <input
                            id="summary"
                            name="summary"
                            type="text"
                            value={formData.summary ?? ""}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="weightGrams">Vikt (gram)</label>
                        <input
                            id="weightGrams"
                            name="weightGrams"
                            type="number"
                            value={formData.weightGrams ?? ""}
                            onChange={handleChange}
                            min="0"
                            step="0.1"
                        />
                    </div>
                </>
            )}

            <div className="form-group">
                <label htmlFor="isDefault">
                    <input
                        id="isDefault"
                        name="isDefault"
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={handleChange}
                    />
                    <span>Ange som standard för kampanjen</span>
                </label>
            </div>

            <div className="form-group">
                <label htmlFor="priority">Prioritet</label>
                <input
                    id="priority"
                    name="priority"
                    type="number"
                    value={formData.priority ?? ""}
                    onChange={handleChange}
                    min="0"
                    aria-invalid={!!errors.priority}
                    aria-describedby={errors.priority ? "priority-error" : undefined}
                    className={errors.priority ? "error-field" : ""}
                />
                {errors.priority && (
                    <span id="priority-error" role="alert" className="error-message">
                        {errors.priority}
                    </span>
                )}
                <small>Lägre nummer = högre prioritet</small>
            </div>

            <div className="form-group">
                <label htmlFor="plannedQuantity">Planerat antal</label>
                <input
                    id="plannedQuantity"
                    name="plannedQuantity"
                    type="number"
                    value={formData.plannedQuantity ?? ""}
                    onChange={handleChange}
                    min="0"
                    aria-invalid={!!errors.plannedQuantity}
                    aria-describedby={
                        errors.plannedQuantity ? "plannedQuantity-error" : undefined
                    }
                    className={errors.plannedQuantity ? "error-field" : ""}
                />
                {errors.plannedQuantity && (
                    <span
                        id="plannedQuantity-error"
                        role="alert"
                        className="error-message"
                    >
                        {errors.plannedQuantity}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label>Bild</label>
                <ImageUpload
                    value={formData.imageUrl ?? ""}
                    onChange={(url) => {
                        setFormData((prev) => ({ ...prev, imageUrl: url }));
                    }}
                    disabled={isLoading}
                />
                <p className="form-hint">
                    Ladda upp en bild för flygbladet.
                </p>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    className="button button--secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Avbryt
                </button>
                <button
                    type="submit"
                    className="button button--primary"
                    disabled={isLoading}
                >
                    {isLoading ? "Sparar..." : isEditMode ? "Spara" : "Lägg till"}
                </button>
            </div>
        </form>
    );
};